import { useRef, useState } from "react";
import {
    Loading,
    SectionHeading,
    TableCustom,
    WrapperAdvancedSearch,
} from "../../components/common";
import { ServerRequest } from "../../utils/apiweb";
import { ALERT_TYPE, OPERATION_MODE, REQUEST_METHOD, ShowToastMessage } from '@laramie/ui';
import { APIS } from "../../config/apis";
import { MESSAGE_MODALS } from "../../context/custom/MessageModalContext";
import { useLista, useManagedContext, useFilterPersistence, useNavigateWithFilters } from "../../components/hooks";
import { PERMISSIONS } from "../../consts/permissions";
import { usePermissions } from "../../components/hooks/usePermissions";
import { createSearchWithSavedFilters } from "../../utils/searchWithSavedFilters";

const RecibosEspecialesView = () => {
    const [loading, setLoading] = useState(false);
    const [tableVisible, setTableVisible] = useState(false);

    const { showMessageModal } = useManagedContext();

    const [state, setState] = useState({
        loading: false,
        list: [],
    });

    const searchRef = useRef();
    const tableRef = useRef();

    const [, getRowLista] = useLista({
        listas: ["TipoTributo"],
        memo: {
            key: "TipoTributo",
            timeout: 0,
        },
    });

    const getDescTipoTributo = (id) => {
        const row = getRowLista("TipoTributo", id);
        return row ? row.nombre : "";
    };

    const { hasPermission } = usePermissions();
    const canEdit = hasPermission(PERMISSIONS.RECIBO_ESPECIAL_EDIT);

    const callbackNoSuccess = (response) => {
        response
            .json()
            .then((error) => {
                ShowToastMessage(ALERT_TYPE.ALERT_ERROR, error.message);
                setState((prevState) => ({ ...prevState, loading: false }));
            })
            .catch((error) => {
                ShowToastMessage(ALERT_TYPE.ALERT_ERROR, `Error procesando respuesta: ${error}`);
                setState((prevState) => ({ ...prevState, loading: false }));
            });
    };

    const callbackError = (error) => {
        ShowToastMessage(ALERT_TYPE.ALERT_ERROR, `Error procesando solicitud: ${error.message}`);
        setState((prevState) => ({ ...prevState, loading: false }));
    };

    const SearchRecibosEspecialesWithSavedFilters = createSearchWithSavedFilters({
        setState,
        setTableVisible,
        apiUrl: APIS.URLS.RECIBO_ESPECIAL,
        buildParamsUrl: (filters) =>
            `/filter?codigo=${encodeURIComponent(filters.codigo || "")}&` +
            `descripcion=${encodeURIComponent(filters.descripcion || "")}&` +
            `idTipoTributo=${filters.idTipoTributo || 0}&` +
            `derechoEspecial=false&` +
            `activo=${filters.activo ? "true" : "false"}`,
        callbackNoSuccess,
        callbackError,
        onSuccess: (savedFilters) => {
            setState((prevState) => {
                let data = prevState.list;
                if (savedFilters.filters.activo) {
                    data = data.filter((f) => f.activo === true);
                }
                return { ...prevState, list: data };
            });
        }
    });

    const { getCurrentFilters, restoredPageIndex } = useFilterPersistence(
        searchRef,
        tableRef,
        SearchRecibosEspecialesWithSavedFilters
    );
    const navigateWithFilters = useNavigateWithFilters(getCurrentFilters);

    const avmr = {
        onAdd: () => navigateWithFilters(`/recibo-especial/${OPERATION_MODE.NEW}`),
        onView: ({ id }) => navigateWithFilters(`/recibo-especial/${OPERATION_MODE.VIEW}/${id}`),
        onModify: ({ id }) => navigateWithFilters(`/recibo-especial/${OPERATION_MODE.EDIT}/${id}`),
        onRemove: ({ id }) => {
            const recibo = state.list.find((x) => x.id === id);
            showMessageModal({
                ...MESSAGE_MODALS.BORRAR,
                message: `¿Está seguro de borrar definición de VEP ${recibo.codigo}?`,
                onConfirm: () => RemoveReciboEspecial(id),
            });
        },
    };

    const SearchRecibosEspeciales = () => {
        setState((prevState) => ({ ...prevState, loading: true, list: [] }));

        const { codigo, descripcion, idTipoTributo, activo } = searchRef.current.filters;

        const callbackSuccess = (response) => {
            response
                .json()
                .then((data) => {
                    if (activo) {
                        data = data.filter((f) => f.activo === true);
                    }
                    setTableVisible(true);
                    setState({ loading: false, list: data });
                })
                .catch((error) => {
                    ShowToastMessage(ALERT_TYPE.ALERT_ERROR, `Error procesando respuesta: ${error}`);
                    setState((prevState) => ({ ...prevState, loading: false }));
                });
        };

        const paramsUrl =
            `/filter?codigo=${encodeURIComponent(codigo || "")}&` +
            `descripcion=${encodeURIComponent(descripcion || "")}&` +
            `idTipoTributo=${idTipoTributo || 0}&` +
            `derechoEspecial=false&` +
            `activo=${activo ? "true" : "false"}`;

        ServerRequest(
            REQUEST_METHOD.GET,
            null,
            true,
            APIS.URLS.RECIBO_ESPECIAL,
            paramsUrl,
            null,
            callbackSuccess,
            callbackNoSuccess,
            callbackError
        );
    };

    const RemoveReciboEspecial = (id) => {
        setState((prevState) => ({ ...prevState, loading: true }));

        const callbackSuccess = () => {
            setState((prevState) => ({ ...prevState, loading: false }));
            SearchRecibosEspeciales();
        };

        const paramsUrl = `/${id}`;

        ServerRequest(
            REQUEST_METHOD.DELETE,
            null,
            true,
            APIS.URLS.RECIBO_ESPECIAL,
            paramsUrl,
            null,
            callbackSuccess,
            callbackNoSuccess,
            callbackError
        );
    };

    return (
        <div>
            <Loading visible={loading} />

            <SectionHeading
                titles={[
                    { title: "Facturación" },
                    { title: "Definición de VEPs especiales" },
                ]}
                onAddClick={canEdit ? avmr.onAdd : null}
                tableVisible={tableVisible}
            />

            <section className="section-accordion">
                <WrapperAdvancedSearch
                    ref={searchRef}
                    filters={[
                        { title: "Código", field: "codigo", type: "string", size: 2, searchOnEnter: true },
                        { title: "Descripción", field: "descripcion", type: "string", size: 3, searchOnEnter: true },
                        {
                            title: "Estado",
                            field: "activo",
                            type: "selectLista",
                            size: 2,
                            options: [
                                { value: null, label: "Todos" },
                                { value: true, label: "Activos" },
                            ],
                        },
                    ]}
                    onSearch={SearchRecibosEspeciales}
                />

                {tableVisible && (
                    <TableCustom
                        ref={tableRef}
                        className="TableCustomBase"
                        data={state.list}
                        restoredPageIndex={restoredPageIndex}
                        messageEmpty="No se encontraron resultados"
                        columns={[
                            {
                                Header: "Tipo de tributo",
                                Cell: ({ value }) => getDescTipoTributo(value),
                                accessor: "idTipoTributo",
                                width: "20%",
                            },
                            { Header: "Código", accessor: "codigo", width: "20%" },
                            { Header: "Descripción", accessor: "descripcion", width: "40%" },
                            {
                                Header: "Aplica UF",
                                accessor: "aplicaValorUF",
                                Cell: ({ value }) => (value ? "Sí" : "No"),
                                width: "10%",
                            },
                            {
                                Header: "Activo",
                                accessor: "activo",
                                Cell: ({ value }) => (value ? "Sí" : "No"),
                                width: "10%",
                            },
                        ]}
                        avmr={avmr}
                        editCode={PERMISSIONS.RECIBO_ESPECIAL_EDIT}
                    />
                )}
            </section>
        </div>
    );
};

export default RecibosEspecialesView;
